export const EXPORT_WINDOW_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

export const LOGGING_STYLE_VALUES = ["fastest", "balanced", "accurate"] as const;
export type LoggingStyleValue = (typeof LOGGING_STYLE_VALUES)[number];

export function loggingStyleUpdate(
  value: string,
): { loggingStyle: LoggingStyleValue } | null {
  return (LOGGING_STYLE_VALUES as readonly string[]).includes(value)
    ? { loggingStyle: value as LoggingStyleValue }
    : null;
}

export const RERUN_ONBOARDING_UPDATE = { onboardingComplete: false } as const;

export interface ExportWindow {
  start: number;
  end: number;
}

export function exportWindow(now: number): ExportWindow {
  return { start: now - EXPORT_WINDOW_DAYS * DAY_MS, end: now };
}

export interface DataExportInput {
  profile: unknown | null;
  foodEntries: readonly unknown[];
  weightHistory: readonly unknown[];
  foodMemories: readonly unknown[];
  generatedAt: number;
}

export function buildDataExportJson(input: DataExportInput): string {
  return JSON.stringify(
    {
      app: "BiteTrack",
      schemaVersion: 1,
      exportedAt: new Date(input.generatedAt).toISOString(),
      windowDays: EXPORT_WINDOW_DAYS,
      profile: input.profile,
      foodEntries: input.foodEntries,
      weightHistory: input.weightHistory,
      foodMemories: input.foodMemories,
    },
    null,
    2,
  );
}

export function exportFilename(now: number): string {
  return `bitetrack-export-${new Date(now).toISOString().slice(0, 10)}.json`;
}

export interface JsonDownloadDeps {
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
  clickAnchor: (href: string, download: string) => void;
}

export function downloadJsonString(
  filename: string,
  json: string,
  deps: JsonDownloadDeps,
): string {
  const blob = new Blob([json], { type: "application/json" });
  const url = deps.createObjectURL(blob);
  deps.clickAnchor(url, filename);
  deps.revokeObjectURL(url);
  return url;
}

export interface RunDataExportArgs {
  fetchEntries: (window: ExportWindow) => Promise<readonly unknown[]>;
  fetchWeights: () => Promise<readonly unknown[]>;
  fetchMemories: () => Promise<readonly unknown[]>;
  profile: unknown | null;
  now: number;
  download: (filename: string, json: string) => void;
}

export type RunDataExportResult =
  | { ok: true; filename: string }
  | { ok: false; message: string };

export async function runDataExport(
  args: RunDataExportArgs,
): Promise<RunDataExportResult> {
  try {
    const [foodEntries, weightHistory, foodMemories] = await Promise.all([
      args.fetchEntries(exportWindow(args.now)),
      args.fetchWeights(),
      args.fetchMemories(),
    ]);
    const json = buildDataExportJson({
      profile: args.profile,
      foodEntries,
      weightHistory,
      foodMemories,
      generatedAt: args.now,
    });
    const filename = exportFilename(args.now);
    args.download(filename, json);
    return { ok: true, filename };
  } catch (error) {
    return {
      ok: false,
      message: error instanceof Error ? error.message : String(error),
    };
  }
}
